import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './CatProd.css';

function CatProd() {
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [columnaOrden, setColumnaOrden] = useState('idCatProd');

  // Paginacion (server-side)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formulario, setFormulario] = useState({
    idCatProd: '',
    nomCatProd: '',
    cuenta: ''
  });
  const [errores, setErrores] = useState({});

  // Importar Excel
  const inputImportarRef = useRef(null);
  const [importando, setImportando] = useState(false);

  const API_URL = '/api/tcatprod';

  // Carga paginada desde el backend
  const cargarDatos = async (opts = {}) => {
    const _pagina    = opts.pagina    ?? pagina;
    const _busqueda  = opts.busqueda  ?? busqueda;
    const _col       = opts.col       ?? columnaOrden;
    const _asc       = opts.asc       ?? ordenAsc;
    const _porPagina = opts.porPagina ?? porPagina;
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: {
          busqueda: _busqueda,
          pagina: _pagina,
          porPagina: _porPagina,
          ordenCol: _col,
          ordenDir: _asc ? 'asc' : 'desc'
        }
      });
      const d = response.data;
      setDatosFiltrados(d.filas || []);
      setTotal(d.total || 0);
      setTotalPaginas(d.totalPaginas || 1);
      setPagina(d.pagina || 1);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    }
  };

  // Carga inicial
  useEffect(() => {
    cargarDatos({ pagina: 1 });
    // eslint-disable-next-line
  }, []);

  // Busqueda con debounce -> vuelve a pagina 1
  useEffect(() => {
    const t = setTimeout(() => {
      cargarDatos({ pagina: 1, busqueda });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [busqueda]);

  const ordenarPor = (columna) => {
    const esAsc = columnaOrden === columna ? !ordenAsc : true;
    setColumnaOrden(columna);
    setOrdenAsc(esAsc);
    cargarDatos({ pagina: 1, col: columna, asc: esAsc });
  };

  const irAPagina = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setPagina(p);
    cargarDatos({ pagina: p });
  };

  const cambiarPorPagina = (n) => {
    setPorPagina(n);
    setPagina(1);
    cargarDatos({ pagina: 1, porPagina: n });
  };

  // Exporta TODO lo que matchea la busqueda (no solo la pagina visible)
  const exportarExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: { busqueda, pagina: 1, porPagina: 100000,
                  ordenCol: columnaOrden, ordenDir: ordenAsc ? 'asc' : 'desc' }
      });
      const datosExportar = (response.data.filas || []).map(item => ({
        'ID Categoría Producto': item.idCatProd,
        'Nombre Categoría Producto': item.nomCatProd,
        'Cuenta': item.cuenta || ''
      }));

      const ws = XLSX.utils.json_to_sheet(datosExportar);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Categoría Productos');
      XLSX.writeFile(wb, 'categoria_productos.xlsx');
    } catch (e) {
      console.error(e);
      alert('Error al exportar');
    }
  };

  // ---------- Importar Excel ----------
  // Auto-detecta la fila de headers. Acepta columnas cuyo texto contenga
  // "cat" (id_catprod / nom_catprod). Si el id viene vacio, busca por
  // nombre y crea si no existe (autoincrement).
  const abrirImportar = () => inputImportarRef.current?.click();

  const onArchivoImportar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const primerSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(primerSheet, { header: 1, defval: '' });

      let headerIdx = -1;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || [];
        const c0 = String(r[0] || '').toLowerCase();
        const c1 = String(r[1] || '').toLowerCase();
        if (c0.includes('cat') && c1.includes('cat')) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) {
        alert('No se encontro fila de encabezados. Debe tener columnas "id_catprod" y "nom_catprod".');
        return;
      }

      const filas = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const id  = String(r[0] ?? '').trim();
        const nom = String(r[1] ?? '').trim();
        if (!id && !nom) continue;
        filas.push({ idCatProd: id, nomCatProd: nom });
      }

      if (filas.length === 0) {
        alert('El archivo no tiene filas de datos.');
        return;
      }

      const conf = window.confirm(
        `Se importaran ${filas.length} categoria(s) de producto. ` +
        `Los ID existentes actualizaran el nombre; si el id viene vacio se busca por nombre ` +
        `y se crea si no existe. No se eliminaran categorias. Continuar?`
      );
      if (!conf) return;

      const { data } = await axios.post('/api/tcatprod/importar', filas);
      const lineas = [
        `Import completado.`,
        `  Creados:      ${data.creados}`,
        `  Actualizados: ${data.actualizados}`,
        `  Ignorados:    ${data.ignorados}`
      ];
      if (data.errores?.length) {
        lineas.push('', 'Errores/advertencias:');
        (data.errores.slice(0, 10)).forEach(m => lineas.push('  - ' + m));
        if (data.errores.length > 10) lineas.push(`  ... y ${data.errores.length - 10} mas`);
      }
      alert(lineas.join('\n'));
      cargarDatos();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al importar: ' + err.message);
    } finally {
      setImportando(false);
      if (inputImportarRef.current) inputImportarRef.current.value = '';
    }
  };

  const abrirModalNuevo = () => {
    setFormulario({ idCatProd: '', nomCatProd: '', cuenta: '' });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (item) => {
    setFormulario({
      idCatProd: item.idCatProd,
      nomCatProd: item.nomCatProd,
      cuenta: item.cuenta || ''
    });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormulario({ idCatProd: '', nomCatProd: '', cuenta: '' });
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errores[name]) {
      setErrores({
        ...errores,
        [name]: ''
      });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    // Validar nombre (requerido y mínimo 3 caracteres)
    if (!formulario.nomCatProd.trim()) {
      nuevosErrores.nomCatProd = 'El nombre es requerido';
    } else if (formulario.nomCatProd.trim().length < 3) {
      nuevosErrores.nomCatProd = 'El nombre debe tener al menos 3 caracteres';
    }

    // Validar cuenta (opcional, pero si se proporciona debe tener formato correcto)
    if (formulario.cuenta && formulario.cuenta.trim()) {
      const cuenta = formulario.cuenta.trim();
      // Validar que solo contenga números, puntos o guiones
      if (!/^[0-9.-]+$/.test(cuenta)) {
        nuevosErrores.cuenta = 'La cuenta solo puede contener números, puntos y guiones';
      }
      if (cuenta.length > 11) {
        nuevosErrores.cuenta = 'La cuenta no puede exceder 11 caracteres';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulario antes de enviar
    if (!validarFormulario()) {
      return;
    }

    // Preparar datos para enviar
    const datosEnviar = {
      nomCatProd: formulario.nomCatProd.trim(),
      cuenta: formulario.cuenta.trim() || null
    };

    // En modo edición, incluir el ID
    if (modoEdicion) {
      datosEnviar.idCatProd = formulario.idCatProd;
    }
    // En modo crear, NUNCA incluir ID - siempre se autogenera

    try {
      if (modoEdicion) {
        await axios.put(`${API_URL}/${formulario.idCatProd}`, datosEnviar);
        alert('Categoría de producto actualizada exitosamente');
      } else {
        await axios.post(API_URL, datosEnviar);
        alert('Categoría de producto creada exitosamente');
      }
      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.response) {
        alert(`Error al guardar: ${error.response.data.message || 'Error del servidor'}`);
      } else if (error.request) {
        alert('Error de conexión con el servidor');
      } else {
        alert('Error al guardar la categoría de producto');
      }
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta categoría de producto?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        alert('Categoría de producto eliminada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar la categoría de producto');
      }
    }
  };

  return (
    <div className="catprod-container">
      <div className="header-seccion">
        <h2>Categoría de Productos</h2>
        <div className="acciones-header">
          <input
            type="text"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
          <button onClick={abrirModalNuevo} className="btn btn-nuevo">
            ➕ Nuevo
          </button>
          <button onClick={exportarExcel} className="btn btn-exportar">
            📊 Exportar Excel
          </button>
          <button onClick={abrirImportar} className="btn btn-exportar"
            disabled={importando}
            style={{ background: '#7B1FA2' }}
            title="Importar categorias desde Excel (upsert por id_catprod, o autoincrement si id vacio)">
            {importando ? '⏳ Importando...' : '📥 Importar Excel'}
          </button>
          <input type="file" accept=".xlsx,.xls" ref={inputImportarRef}
            onChange={onArchivoImportar} style={{ display: 'none' }} />
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-datos">
          <thead>
            <tr>
              <th onClick={() => ordenarPor('idCatProd')} className="th-ordenable">
                ID {columnaOrden === 'idCatProd' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('nomCatProd')} className="th-ordenable">
                Nombre {columnaOrden === 'nomCatProd' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('cuenta')} className="th-ordenable">
                Cuenta {columnaOrden === 'cuenta' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {datosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="4" className="sin-datos">No hay datos para mostrar</td>
              </tr>
            ) : (
              datosFiltrados.map(item => (
                <tr key={item.idCatProd}>
                  <td>{item.idCatProd}</td>
                  <td>{item.nomCatProd}</td>
                  <td>{item.cuenta || ''}</td>
                  <td>
                    <button
                      onClick={() => abrirModalEditar(item)}
                      className="btn-accion btn-editar"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminar(item.idCatProd)}
                      className="btn-accion btn-eliminar"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="footer-tabla" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span>Total de registros: {total}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => irAPagina(1)} disabled={pagina === 1} className="btn-paginacion">«</button>
          <button onClick={() => irAPagina(pagina - 1)} disabled={pagina === 1} className="btn-paginacion">‹</button>
          <span style={{ fontSize: '13px' }}>Pagina {pagina} de {totalPaginas}</span>
          <button onClick={() => irAPagina(pagina + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">›</button>
          <button onClick={() => irAPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">»</button>
          <select value={porPagina} onChange={(e) => cambiarPorPagina(Number(e.target.value))}
            style={{ marginLeft: '8px', padding: '4px' }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
          </select>
        </div>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modoEdicion ? 'Editar Categoría de Producto' : 'Nueva Categoría de Producto'}</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* El ID se autogenera siempre, no se muestra en modo crear */}

              <div className="form-group">
                <label>Nombre Categoría Producto *</label>
                <input
                  type="text"
                  name="nomCatProd"
                  value={formulario.nomCatProd}
                  onChange={handleChange}
                  maxLength="100"
                  className={`form-input ${errores.nomCatProd ? 'form-input-error' : ''}`}
                />
                {errores.nomCatProd && (
                  <small className="form-error">{errores.nomCatProd}</small>
                )}
              </div>

              <div className="form-group">
                <label>Cuenta</label>
                <input
                  type="text"
                  name="cuenta"
                  value={formulario.cuenta}
                  onChange={handleChange}
                  maxLength="11"
                  className={`form-input ${errores.cuenta ? 'form-input-error' : ''}`}
                  placeholder="Ej: 123.45.678"
                />
                {errores.cuenta && (
                  <small className="form-error">{errores.cuenta}</small>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-guardar">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CatProd;
